/**
 * Die Lieferantenflaeche - NEUBAU, kein Nachbau (Task 11; Befund 15, Option B, entschieden
 * 2026-08-11 vom Auftraggeber).
 *
 * Fachliche Festlegung, woertlich: "JTL-Lieferanten nur lesend anzeigen/suchen, Tool-Zusatzfelder
 * (`supplier_extras`) pflegbar; ansehen duerfen alle, pflegen nur `manage_suppliers`."
 * (`docs/superpowers/decisions/2026-08-09-auffaelligkeiten-entscheidungsvorlage.md`, Befund 15;
 * deckungsgleich in `test/befunde/registratur.ts#BEFUND_15`.)
 *
 * ============================== WAS ES AN ROUTEN WIRKLICH GIBT ==============================
 *
 * Selbst gemessen, nicht aus dem Brief uebernommen (`backend/src/http/endpunktMatrix.ts:105-106`
 * und `backend/src/http/routes/jtl.ts:92,104`):
 *
 *   GET   /api/suppliers                    Uebersichtsliste fuer die Tabelle (seit 2026-10-01)
 *   GET   /api/suppliers/:kLieferant        JTL-Stammdaten UND `supplier_extras`, getrennt
 *   PATCH /api/suppliers/:kLieferant/extras Schreiben, Recht `manage_suppliers`
 *
 * ============================== DIE UEBERSICHT (seit 2026-10-01) ==============================
 *
 * Ohne gewaehlten Lieferanten zeigt die Seite `LieferantenTabelle`: alle Lieferanten mit ihren
 * Zahlungszielen, direkt aenderbar. Das Suchfeld filtert diese Tabelle im Browser (Firmenname, ohne
 * Mindestlaenge); die fruehere JTL-Suche dieser Seite ist entfallen (`GET /api/jtl/suppliers`
 * bleibt fuer die Anlagemaske). Ein Klick auf den Namen setzt `id` in der Adresse, "Alle
 * Lieferanten" in der Detailansicht setzt sie zurueck.
 *
 * Benannte Luecke: der Task-Brief skizzierte eine Prop `onOeffnen?: (bestellungId: string) => void`.
 * Sie ist hier NICHT gebaut, weil die Flaeche BEWUSST keine Bestellungen zeigt - es gaebe nichts, das
 * den Rueckruf ausloeste, und eine Prop ohne Ausloeser ist ein toter Draht.
 *
 * ============================== RECHTE ==============================
 *
 * `GET /api/suppliers/:kLieferant` traegt `null`-Rechte (Constraint 9: "Lesen steht allen Rollen
 * offen"), `PATCH …/extras` verlangt `manage_suppliers`. Diese Seite ist deshalb fuer JEDES
 * angemeldete Konto erreichbar; nur `ExtrasFormular` sperrt - ausgegraut mit Hinweis, nicht erst
 * beim Klick (Entscheidung des Auftraggebers vom 2026-08-11).
 */
import { useEffect, useState, type JSX } from "react";
import { extrasFlach, lieferantLaden } from "../../api/lieferanten.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { useEntprelltenAdresswert } from "../../state/useEntprelltenAdresswert.ts";
import { AKTIV_BESCHRIFTUNG, aktivZustand } from "../../lib/jtlAktiv.ts";
import { NUM } from "../../lib/format.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { Supplier } from "../../lib/types.ts";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { ExtrasFormular } from "./ExtrasFormular.tsx";
import { LieferantenTabelle } from "./LieferantenTabelle.tsx";
import type { FreigabeZeile } from "../../api/lieferantenZahlungsziele.ts";

/**
 * Die JTL-Stammdaten, die `GET /api/suppliers/:kLieferant` unter `jtl` liefert - Feld fuer Feld aus
 * `backend/src/jtl/typen.ts#JtlLieferant` (:3-13) uebernommen. Sie sind hier AUSSCHLIESSLICH
 * Anzeige: das Tool legt in JTL nichts an und aendert dort nichts (Sicherheitsgrenze des Projekts:
 * JTL nur lesend).
 *
 * `kLieferant` fehlt in dieser Tabelle bewusst - die Kennung steht als Ueberschrift der Karte, nicht
 * ein zweites Mal als Datenzeile.
 */
const JTL_STAMMFELDER: readonly { name: string; label: string; anzeigen?: (wert: unknown) => string }[] = [
  { name: "firma", label: "Firma" },
  { name: "kontakt", label: "Kontakt" },
  { name: "email", label: "E-Mail" },
  { name: "lieferzeitTage", label: "Lieferzeit (Tage)" },
  { name: "zahlungszielTage", label: "Zahlungsziel (Tage)" },
  { name: "skontoProzent", label: "Skonto (%)" },
  { name: "mindestbestellwert", label: "Mindestbestellwert" },
  // Das EINZIGE Feld mit eigener Umformung, und der Grund ist gemessen: `aktiv` ist in JTL kein
  // Boolean, sondern `JtlAktiv = "Y" | "N" | null` (`backend/src/jtl/werte.ts:5`, gefuellt aus
  // `tlieferant.cAktiv` ueber `alsAktiv`). `anzeige()` unten uebersetzt nur echte Booleans - bis zur
  // Fix-Runde 1 stand deshalb ein rohes "Y" unter "Aktiv" in einer deutschen Oberflaeche.
  { name: "aktiv", label: "Aktiv", anzeigen: jaNeinAusJtl },
];

/**
 * Ein fehlender Wert wird als Gedankenstrich gezeigt, nie als "undefined" oder leere Zelle.
 *
 * Zahlen ueber `NUM` (deutsches Format, Auftrag "verbleibende Zahlenformat-Luecken auf deutsches
 * Format bringen", 2026-09-07) - betrifft die vier numerischen JTL-Rohfelder ohne eigene
 * `anzeigen`-Funktion (`lieferzeitTage`, `zahlungszielTage`, `skontoProzent`, `mindestbestellwert`,
 * `backend/src/jtl/typen.ts:8-11`, alle `number | null`). `NUM` statt `EUR`/`EUR0` fuer
 * `mindestbestellwert`: die Tabellenzeile haengt kein Waehrungssymbol an (siehe Render-Stelle
 * unten, `<dd>{(feld.anzeigen ?? anzeige)(...)}</dd>`), ein "€" waere hier ein zusaetzliches
 * UI-Element, das der Auftrag nicht verlangt - reine Zahlenformatierung ist die konservativere Wahl.
 */
function anzeige(wert: unknown): string {
  if (wert === null || wert === undefined || wert === "") return "–";
  if (typeof wert === "boolean") return wert ? "ja" : "nein";
  if (typeof wert === "number") return NUM.format(wert);
  return String(wert);
}

/**
 * Die Kachel ohne Logo zeigt bis zu zwei Anfangsbuchstaben. `aria-hidden`, weil der Firmenname
 * unmittelbar daneben steht - sonst laese ein Screenreader ihn zweimal an.
 */
function initialen(firma: string): string {
  return firma
    .split(/\s+/)
    .filter((teil) => /[a-zA-ZÄÖÜäöüß0-9]/.test(teil))
    .slice(0, 2)
    .map((teil) => teil[0]!.toUpperCase())
    .join("");
}

/**
 * `JtlAktiv` ("Y" | "N" | null) in die Sprache der Oberflaeche. Ein unbekannter Wert faellt auf
 * `anzeige()` zurueck statt still zu "nein" zu werden: waere die Spalte eines Tages nicht mehr
 * `Y`/`N`, soll der unbekannte Wert SICHTBAR sein und nicht als plausible Unwahrheit dastehen.
 *
 * BERICHTIGT am 2026-08-13: der Vergleich auf "Y"/"N" stand bis dahin HIER. Seit die Trefferlisten
 * denselben Zustand deuten (`AktivMarke`, zwei Suchfelder), wohnt die Einordnung in
 * `lib/jtlAktiv.ts` - drei Orte mit demselben Vergleich waeren die Bauform, aus der der Befund der
 * Fix-Runde 2 ein zweites Mal entstuende.
 */
function jaNeinAusJtl(wert: unknown): string {
  const zustand = aktivZustand(wert);
  if (zustand === "aktiv") return "ja";
  if (zustand === "inaktiv") return "nein";
  return anzeige(wert);
}

/**
 * Klasse und Text der Kopfkarten-Pille - aus derselben Einordnung wie `jaNeinAusJtl` und wie die
 * Marke an einem Suchtreffer, NICHT aus einem eigenen Vergleich auf "Y"/"N": eine Regel, ein Ort.
 * Fix-Runde 2 (2026-08-13): bis dahin galt alles ausser "N" als aktiv - bei `aktiv: null` zeigte die
 * Kopfkarte eine gruene Pille "AKTIV", waehrend die Stammdatenzeile zwei Zentimeter darunter "–"
 * zeigte.
 */
function pilleAusAktiv(aktiv: unknown): { klasse: string; text: string } {
  const zustand = aktivZustand(aktiv);
  const text = AKTIV_BESCHRIFTUNG[zustand];
  if (zustand === "aktiv") return { klasse: "suppliers-pille aktiv", text };
  if (zustand === "inaktiv") return { klasse: "suppliers-pille inaktiv", text };
  return { klasse: "suppliers-pille", text };
}

export interface LieferantenSeiteProps {
  rechte: ReadonlySet<string>;
  onFreigabenGespeichert?: (kLieferant: number, freigaben: FreigabeZeile[]) => void;
}

export function LieferantenSeite({ rechte, onFreigabenGespeichert }: LieferantenSeiteProps): JSX.Element {
  // Suchbegriff und gewaehlter Lieferant leben in der ADRESSE (Spezifikation § 3.3, Aufgabe 4) -
  // aber NUR dessen `id`, nicht der ganze Datensatz (siehe Kopfkommentar zu `lieferant` unten).
  const { ansicht, setzeAnsicht } = useAnsicht();
  const { q: begriff, id: lieferantenId } = ansicht.lieferanten;
  // BEIDE ueber die FUNKTIONSFORM (`alt => …`), nicht ueber ein fertiges Objekt: der verzoegerte
  // Suchschreiber unten kann auf einem VORHERIGEN `ansicht`-Stand schliessen, waehrend ein Klick auf
  // einen Namen schon die `id` gesetzt hat, und wuerde sie mit einem fertigen Objekt wieder
  // zuruecksetzen. Die Funktionsform liest immer den STAND, auf dem `setzeAnsicht` GERADE rechnet
  // (`useAnsicht.ts`, Kommentar bei `aktuellerStand`), nie einen veralteten Ausschnitt.
  const setzeLieferantenSuche = (wert: string): void =>
    setzeAnsicht((alt) => ({ lieferanten: { ...alt.lieferanten, q: wert } }), { ersetzen: true });
  // Einen Lieferanten oeffnen ist wie `drawerOeffnen` (`App.tsx`) eine echte Navigation - PUSH, nicht
  // ERSETZEN (kein zweites Argument): die Zurueck-Taste soll zur Liste zurueckfuehren.
  const waehleLieferant = (id: string | null): void =>
    setzeAnsicht((alt) => ({ lieferanten: { ...alt.lieferanten, id } }));

  // Der Sofortwert fuers Eingabefeld; die Adresse zieht 300 ms spaeter nach (`useEntprelltenAdresswert`).
  const [begriffRoh, setBegriffRoh] = useEntprelltenAdresswert(begriff, setzeLieferantenSuche);

  /**
   * Der VOLLE Datensatz - anders als `bestellungNachKennung` (`App.tsx`) keine SYNCHRONE Suche in
   * einer bereits geladenen Liste: die Uebersicht kennt nur Kennung, Firma und Zahlungsziele, die
   * Detailansicht braucht JTL-Stammdaten und Extras. Die Aufloesung von `lieferantenId` auf einen Datensatz laeuft deshalb ASYNCHRON
   * ueber `oeffne()` im Effekt unten - dieselbe Aufloesung wie bei der Bestellung, nur mit einem
   * Netzwerkschritt dazwischen statt eines `Array.find`.
   */
  const [lieferant, setLieferant] = useState<Supplier | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [zaehlung, setZaehlung] = useState<{ treffer: number; gesamt: number } | null>(null);

  const darfSchreiben = hatRecht(rechte, "manage_suppliers");

  async function oeffne(kLieferant: string): Promise<void> {
    setMeldung(null);
    try {
      const geladen = await lieferantLaden(kLieferant);
      if (geladen === null) {
        setLieferant(null);
        setMeldung(`Lieferant ${kLieferant} ist nicht (mehr) abrufbar.`);
        return;
      }
      setLieferant(geladen);
    } catch (grund) {
      setLieferant(null);
      // Die Meldungszeile gab es schon (R2): neu ist der Text. Der Satz zum Scheitern wird zum Kontext.
      setMeldung(fehlertext(grund, "Der Lieferant konnte nicht geladen werden."));
    }
  }

  // Aufloesung der Kennung aus der Adresse (siehe Kopfkommentar bei `lieferant` oben) - laedt bei
  // jedem Rendern nach, bei dem `lieferantenId` von der zuletzt geladenen Kennung abweicht. Deckt
  // sowohl eine Auswahl aus der Suche (`waehleLieferant`) als auch einen Tieflink beim ersten
  // Rendern mit demselben Codepfad ab.
  useEffect(() => {
    if (lieferantenId === null) {
      setLieferant(null);
      setMeldung(null);
      return;
    }
    if (lieferant !== null && lieferant.id === lieferantenId) return;
    void oeffne(lieferantenId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lieferantenId]);

  /*
   * `rowVersion` liegt nach `entschachtele()` flach im Datensatz und stammt IMMER aus der
   * `supplier_extras`-Zeile (JTL-Stammdaten sind nicht versioniert - siehe `api/lieferanten.ts`,
   * Abschnitt KOLLISIONSFRAGE). Fehlt sie, gibt es noch keine Zeile: `null` ist genau der Wert, mit
   * dem der Server sie anlegt - eine Umdeutung auf `0` waere ein Konflikt mit einer Zeile, die es
   * nicht gibt.
   */
  const rowVersion =
    lieferant && typeof lieferant.rowVersion === "number" ? (lieferant.rowVersion as number) : null;

  /**
   * DER RUECKWEG DES EINZIGEN SCHREIBPFADES - und die Stelle, an der `rowVersion` fortgeschrieben
   * wird.
   *
   * Der Server antwortet auf den PATCH mit der frischen, oeffentlichen `supplier_extras`-Zeile
   * (`oeffentlich(zeile)`, `backend/src/http/routes/suppliers.ts:83`) - inklusive der NEUEN
   * `rowVersion`. Sie wird UEBER den bisherigen Stand gelegt, statt den ganzen Datensatz neu zu
   * laden: die JTL-Haelfte hat sich nicht geaendert, und ein zweiter Abruf ginge erneut in die
   * Fremd-Datenbank.
   *
   * OHNE DIESE FORTSCHREIBUNG bliebe `rowVersion` auf dem Lesestand stehen. Zweimal hintereinander
   * speichern ergaebe beim ZWEITEN Mal einen echten 409 ("Zwischenzeitlich fremd geaendert"),
   * obwohl niemand fremd geaendert hat - der Fehlerfall, den `verwaltung.test.tsx` seit der
   * Fix-Runde 1 mit einem eigenen Fall haelt ("schreibt die neue rowVersion fort").
   *
   * `extrasFlach()` STATT EINES ROHEN SPREADS (Fix-Runde 1): der Leseweg entfernt `kLieferant` aus
   * der Zeile und laesst die JTL-Kennung ausschliesslich als `id` stehen (`entschachtele()`,
   * `api/lieferanten.ts`). Ein roher Spread der Serverantwort traege sie zurueck - derselbe
   * Datensatz saehe nach dem Speichern anders aus als nach dem Lesen. Beide Wege gehen deshalb durch
   * DIESELBE Funktion; die uebrigen Schluessel der Zeile (`eur`, `shortName`, ..., `rowVersion`,
   * `updatedAt`, `updatedBy`) uebernimmt sie unveraendert.
   */
  function uebernimmGespeicherteZeile(zeile: Record<string, unknown>): void {
    const uebernommen = extrasFlach(zeile);
    setLieferant((alt) => (alt === null ? alt : { ...alt, ...uebernommen }));
  }

  return (
    <div className="suppliers-shell">
      <Seitenkopf titel="Lieferanten" unterzeile="Stammdaten aus JTL, Zusatzfelder im Werkzeug" />
      {lieferantenId === null ? (
        <>
          <div className="suppliers-suche field">
            <label htmlFor="supplierSearch">Lieferant suchen</label>
            <div className="suppliers-suchzeile">
              <input
                id="supplierSearch"
                type="search"
                autoComplete="off"
                value={begriffRoh}
                placeholder="Firmenname"
                onChange={(e) => {
                  setBegriffRoh(e.target.value);
                  setMeldung(null);
                }}
              />
              {zaehlung !== null ? (
                <span className="suppliers-zaehler" aria-live="polite">
                  {zaehlung.treffer} von {zaehlung.gesamt}
                </span>
              ) : null}
            </div>
          </div>
          <LieferantenTabelle
            darfSchreiben={darfSchreiben}
            filter={begriffRoh}
            onOeffnen={(k) => waehleLieferant(String(k))}
            onFreigabenGespeichert={onFreigabenGespeichert}
            onZaehlung={(treffer, gesamt) => setZaehlung({ treffer, gesamt })}
          />
        </>
      ) : (
        <div className="suppliers-zurueck">
          <button type="button" className="suppliers-zurueck-knopf" onClick={() => waehleLieferant(null)}>
            ← Alle Lieferanten
          </button>
        </div>
      )}

      {meldung !== null ? (
        <p className="suppliers-meldung" role="alert">
          {meldung}
        </p>
      ) : null}

      {lieferantenId !== null && lieferant !== null ? (
        <div className="suppliers-detail">
          <div className="suppliers-kopf">
            {typeof lieferant.logo === "string" && lieferant.logo.trim() !== "" ? (
              <img className="supplier-logo" src={lieferant.logo} alt="" />
            ) : (
              <div className="suppliers-kopf-initialen" aria-hidden="true">
                {initialen(anzeige(lieferant.firma))}
              </div>
            )}
            <div className="suppliers-kopf-text">
              <h2 className="suppliers-titel">
                {anzeige(lieferant.firma)} <span className="mono">#{anzeige(lieferant.id)}</span>
              </h2>
              <div className="suppliers-chips">
                {(() => {
                  const { klasse, text } = pilleAusAktiv(lieferant.aktiv);
                  return <span className={klasse}>{text}</span>;
                })()}
                {typeof lieferant.shortName === "string" && lieferant.shortName.trim() !== "" ? (
                  <span className="suppliers-pille">{lieferant.shortName}</span>
                ) : null}
                {lieferant.eur === true ? <span className="suppliers-pille">EUR</span> : null}
              </div>
            </div>
          </div>
          {/*
            SCHREIBGESCHUETZT UND ZWAR STRUKTURELL: eine Beschreibungsliste, kein einziges
            Bedienelement. Ein `disabled`-Eingabefeld waere die schwaechere Zusage - es liesse sich
            per DevTools freischalten, und es gibt fuer JTL-Stammdaten ohnehin keinen Schreibweg
            (Sicherheitsgrenze: JTL nur lesend).
          */}
          <dl className="suppliers-stamm">
            {JTL_STAMMFELDER.map((feld) => (
              <div className="suppliers-stammzeile" key={feld.name}>
                <dt>{feld.label}</dt>
                <dd>{(feld.anzeigen ?? anzeige)(lieferant[feld.name])}</dd>
              </div>
            ))}
          </dl>
          {/*
            DIE `key` IST DER EINZIGE RUECKSETZWEG DES FORMULARS - und die Behebung des am
            2026-08-13 gemeldeten Fehlers ("wenn ich den Lieferanten oben wechsle, bleiben die alten
            Werte einfach stehen").

            VORHER gab es ZWEI Ruecksetzwege, und sie griffen in verschiedener Reihenfolge: ein
            `useEffect` in `ExtrasFormular` (NACH der Zeichnung) und eine eigene `key` an
            `ZahlungsplanFeld` (WAEHREND der Zeichnung). Die Ratenmaske hing sich damit in genau der
            Zeichnung neu ein, in der der Entwurf noch den VORIGEN Lieferanten trug - und las ihren
            Text nie wieder nach. Wer danach eine Rate anfasste, schrieb den Plan des vorigen
            Lieferanten in den Datensatz des neuen.

            EIN Ausloeser, EIN Weg: wechselt die Kennung (anderer Lieferant) oder die `rowVersion`
            (erfolgreich gespeichert), baut React das Formular samt Ratenmaske neu - aus den frischen
            Props, ohne Zwischenzeichnung mit altem Zustand.

            DER 409-FALL BLEIBT UNBERUEHRT, und das ist die Bedingung der Drei-Wege-Absage: dort
            aendert sich weder die Kennung noch die `rowVersion`, es wird also NICHTS neu eingehaengt
            - die eingetippten Werte bleiben stehen, waehrend daneben der Serverstand erscheint
            (Fall "laesst nach einem 409 die Eingaben stehen", `verwaltung.test.tsx`).
          */}
          <ExtrasFormular
            key={`${String(lieferant.id)}-${rowVersion ?? "neu"}`}
            kLieferant={String(lieferant.id)}
            extras={lieferant as Record<string, unknown>}
            rowVersion={rowVersion}
            darfSchreiben={darfSchreiben}
            onGespeichert={uebernimmGespeicherteZeile}
            onFreigabenGespeichert={onFreigabenGespeichert}
          />
        </div>
      ) : null}
    </div>
  );
}
